import { lazy, Suspense, useEffect, useLayoutEffect, useRef } from "react";
import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import { AnnouncementTicker } from "@/components/AnnouncementTicker";
import { ChatAssistant } from "@/components/assistant/ChatAssistant";
import { CookieBanner } from "@/components/legal/CookieBanner";
import { Footer } from "@/components/layout/Footer";
import { Header } from "@/components/layout/Header";
import { MobileCtaBar } from "@/components/layout/MobileCtaBar";
import { CartDrawer } from "@/components/store/CartDrawer";
import { RevealManager } from "@/components/ui/RevealManager";
import { ADVISORY_FORM_PATH, LINK_HUB_PATH, QUIZ_PATH } from "@/config/constants";
import { SHOW_SISTEMA_LUCENTIA } from "@/config/features";
import { useHydrated } from "@/hooks/useHydrated";
import { useAppTranslation } from "@/i18n/useAppTranslation";
import { AdvisoryFormPage } from "@/pages/AdvisoryFormPage";
import { BlogIndexPage } from "@/pages/BlogIndexPage";
import { BlogPostPage } from "@/pages/BlogPostPage";
import { BlogRoutePage } from "@/pages/BlogRoutePage";
import { HomePage } from "@/pages/HomePage";
import { LinkHubPage } from "@/pages/LinkHubPage";
import { NichePage } from "@/pages/NichePage";
import { NICHES } from "@/data/niches";
import { QuizPage } from "@/pages/QuizPage";
import { InstagramAdvisoryPage } from "@/pages/InstagramAdvisoryPage";
import { InstagramManagementPage } from "@/pages/InstagramManagementPage";
import { LegalPage as LegalPageInline } from "@/pages/LegalPage";
import { MethodPage } from "@/pages/MethodPage";
import { NotFoundPage } from "@/pages/NotFoundPage";
import { PricingPage } from "@/pages/PricingPage";
import { MyPurchasesPage } from "@/pages/MyPurchasesPage";
import { PurchaseCompletePage } from "@/pages/PurchaseCompletePage";
import { ResourcesPage } from "@/pages/ResourcesPage";
import { WebServicePage } from "@/pages/WebServicePage";
import { trackEvent } from "@/utils/analytics";
import { onCalendlyBooked } from "@/utils/calendly";
import { installClickTracking } from "@/utils/clickTracking";
import { subscribeCookieConsent } from "@/utils/cookieConsent";
import { scrollToSectionIdWhenReady } from "@/utils/smoothScroll";

const AdminPage = lazy(() => import("@/pages/AdminPage"));
/* Legal texts are long and rarely visited: in the browser they load with their
   own chunk; the prerenderer renders them inline so the HTML is complete. */
const LegalPage = import.meta.env.SSR
  ? LegalPageInline
  : lazy(() => import("@/pages/LegalPage").then((m) => ({ default: m.LegalPage })));

function isAdminPath(pathname: string) {
  return pathname === "/admin" || pathname.startsWith("/admin/");
}

function PageViewTracker() {
  const location = useLocation();
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;

  useEffect(() => {
    if (isAdminPath(location.pathname)) return;
    trackEvent("page_view", { path: location.pathname });
  }, [location.pathname]);

  /* The landing page_view fires before the visitor answers the cookie banner and
     is dropped; re-send it for the current page once analytics consent is granted. */
  useEffect(() => {
    return subscribeCookieConsent((consent) => {
      if (consent?.analytics && !isAdminPath(pathRef.current)) {
        trackEvent("page_view", { path: pathRef.current });
      }
    });
  }, []);

  /* A completed booking in the Calendly popup is the site's real conversion. */
  useEffect(() => onCalendlyBooked(() => trackEvent("calendly_booked", { path: pathRef.current })), []);

  /* Important clicks: CTAs, WhatsApp, outbound links, FAQ questions. */
  useEffect(() => installClickTracking(), []);

  return null;
}

/** New pages start at the top; `/#section` links scroll once the section exists. */
function ScrollManager() {
  const { pathname, hash } = useLocation();
  const first = useRef(true);

  useLayoutEffect(() => {
    if (first.current) {
      first.current = false;
      if (!hash) return;
    }
    if (hash) {
      scrollToSectionIdWhenReady(hash.slice(1));
      return;
    }
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname, hash]);

  return null;
}

/* Same Suspense wrapper on server and client so hydration markers match. */
function LegalRoute() {
  return (
    <Suspense fallback={<main id="main-content" className="min-h-screen" />}>
      <LegalPage />
    </Suspense>
  );
}

function Layout() {
  const { t } = useAppTranslation("common");
  const hydrated = useHydrated();

  return (
    <div className="min-h-screen bg-brand-bg pt-[var(--banner-h,0px)] font-sans text-ink">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-[calc(var(--banner-h,0px)+0.75rem)] focus:z-[100] focus:rounded-full focus:bg-brand-primary focus:px-5 focus:py-3 focus:text-sm focus:font-semibold focus:text-white"
      >
        {t("skipToContent")}
      </a>
      <AnnouncementTicker />
      <Header />
      <Outlet />
      <Footer />
      {hydrated ? <MobileCtaBar /> : null}
      {hydrated ? <ChatAssistant /> : null}
      {hydrated ? <CartDrawer /> : null}
    </div>
  );
}

function AdminFallback() {
  const { t } = useAppTranslation("common");
  return (
    <div className="flex min-h-screen items-center justify-center bg-brand-bg">
      <p className="text-sm text-ink-muted">{t("loadingPanel")}</p>
    </div>
  );
}

export default function App() {
  const location = useLocation();

  return (
    <>
      <PageViewTracker />
      <ScrollManager />
      <RevealManager />
      {!isAdminPath(location.pathname) && <CookieBanner />}
      <Routes>
        <Route
          path="/admin"
          element={
            <Suspense fallback={<AdminFallback />}>
              <AdminPage />
            </Suspense>
          }
        />
        {/* The bio link hub is a single screen: no header, footer or assistant. */}
        <Route path={LINK_HUB_PATH} element={<LinkHubPage />} />
        <Route element={<Layout />}>
          <Route path="/" element={<HomePage />} />
          {SHOW_SISTEMA_LUCENTIA ? <Route path="/metodo" element={<MethodPage />} /> : null}
          <Route path="/precios" element={<PricingPage />} />
          <Route path="/pagina-web" element={<WebServicePage />} />
          <Route path="/gestion-instagram" element={<InstagramManagementPage />} />
          <Route path="/asesoria-instagram" element={<InstagramAdvisoryPage />} />
          <Route path={ADVISORY_FORM_PATH} element={<AdvisoryFormPage />} />
          <Route path={QUIZ_PATH} element={<QuizPage />} />
          {NICHES.map((niche) => (
            <Route key={niche.id} path={niche.path} element={<NichePage />} />
          ))}
          <Route path="/recursos" element={<ResourcesPage />} />
          <Route path="/recursos/compra" element={<PurchaseCompletePage />} />
          <Route path="/recursos/mis-compras" element={<MyPurchasesPage />} />
          <Route path="/resources" element={<Navigate to="/recursos" replace />} />
          <Route path="/blog" element={<BlogIndexPage />} />
          <Route path="/blog/ruta/:routeId" element={<BlogRoutePage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/aviso-legal" element={<LegalRoute />} />
          <Route path="/privacidad" element={<LegalRoute />} />
          <Route path="/cookies" element={<LegalRoute />} />
          <Route path="/condiciones" element={<LegalRoute />} />
          <Route path="/accesibilidad" element={<LegalRoute />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </>
  );
}
